// شاشة الإعداد الأولي — تظهر مرة واحدة فقط عند أول استخدام للحساب
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLockup from "@/v2/components/BrandLockup";
import DeptPicker from "@/v2/components/DeptPicker";
import { Confetti } from "@/v2/components/ui";
import { errText } from "@/v2/lib/hooks";
import "@/v2/v2.css";
import {
  Sparkles, Building2, ArrowLeft, ArrowRight, HardHat, Laptop, Crown, CheckCircle2, Send, Clock,
} from "lucide-react";

const TYPES = [
  { key: "field", label: "موظف ميداني", icon: HardHat,
    desc: "مسار يركز على المهارات السلوكية والميدانية والتعامل مع بيئة العمل اليومية." },
  { key: "office", label: "موظف مكتبي", icon: Laptop,
    desc: "مسار يركز على المهارات الفنية والأدوات التي ترفع كفاءتك في العمل المكتبي." },
  { key: "manager", label: "مدير إدارة", icon: Crown,
    desc: "مسار قيادي يركز على التخطيط وإدارة المهام وتطوير أداء الفريق." },
];

const Steps = ({ step }) => (
  <div className="flex items-center gap-2 mb-8" data-testid="onb-progress">
    {[0, 1, 2].map((i) => (
      <div key={i} className="h-1.5 rounded-full transition-all duration-500"
        style={{ width: i === step ? 44 : 22, background: i <= step ? "linear-gradient(90deg,#91cab8,#e3c799)" : "rgba(255,255,255,0.12)" }} />
    ))}
  </div>
);

export default function Onboarding() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dept, setDept] = useState(null);
  const [typedDept, setTypedDept] = useState("");
  const [type, setType] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/onboarding", {
        department_id: dept?.id || "", department_text: typedDept, employee_type: type,
      });
      setDone(data.status);
      if (data.status === "approved") {
        toast.success("تم اعتماد حسابك — رحلتك جاهزة!");
        setTimeout(async () => { await refresh(); navigate("/dashboard", { replace: true }); }, 1800);
      }
    } catch (e) { toast.error(errText(e, "تعذّر إرسال الطلب")); } finally { setSending(false); }
  };

  const goPending = async () => { await refresh(); navigate("/pending", { replace: true }); };

  return (
    <div className="v2-root min-h-screen flex flex-col items-center justify-center p-5 sm:p-8 v2-grid v2-pattern relative overflow-hidden" dir="rtl">
      <div className="absolute top-[-15%] left-[-10%] w-[520px] h-[520px] rounded-full bg-[#91cab8]/12 blur-3xl v2-float pointer-events-none" />
      <div className="absolute bottom-[-15%] right-[-5%] w-[420px] h-[420px] rounded-full bg-[#bb9661]/10 blur-3xl v2-float pointer-events-none" style={{ animationDelay: "2s" }} />

      <div className="w-full max-w-2xl relative z-10">
        <div className="flex items-center justify-between gap-5 flex-wrap mb-8">
          <BrandLockup tone="dark" />
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="onb-logout"
            className="text-xs text-[#bcc8cf] hover:text-white">تسجيل الخروج</button>
        </div>

        <div className="v2-glass v2-glass-gold p-6 sm:p-10 relative overflow-hidden" data-testid="onboarding-card">
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6 relative">
              {done === "approved" && <Confetti />}
              <div className="w-20 h-20 mx-auto rounded-full bg-[#91cab8]/15 text-[#91cab8] flex items-center justify-center mb-5">
                {done === "approved" ? <CheckCircle2 size={40} /> : <Clock size={40} />}
              </div>
              {done === "approved" ? (
                <>
                  <h2 className="text-3xl font-extrabold mb-3">تم اعتماد حسابك 🎉</h2>
                  <p className="text-[#bcc8cf] max-w-md mx-auto">جهزنا لك رحلة تعليمية تناسب مسارك — جارٍ تحويلك إلى لوحتك.</p>
                </>
              ) : (
                <>
                  <h2 className="text-3xl font-extrabold mb-3" data-testid="onb-success">تم استلام طلبك بنجاح</h2>
                  <p className="text-[#bcc8cf] max-w-md mx-auto leading-relaxed">
                    طلبك الآن قيد المراجعة، وسيتم تفعيل حسابك بعد موافقة إدارة المنصة.
                  </p>
                  <button onClick={goPending} data-testid="onb-to-pending" className="v2-btn mt-7">
                    متابعة حالة الطلب <ArrowLeft size={16} />
                  </button>
                </>
              )}
            </motion.div>
          ) : (
            <>
              <Steps step={step} />
              <AnimatePresence mode="wait">
                {step === 0 && (
                  <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.35 }}>
                    <div className="inline-flex mb-5"><span className="v2-glass px-4 py-1.5 rounded-full text-xs text-[#91cab8] border-[#91cab8]/30 flex items-center gap-2"><Sparkles size={14} /> خطوة واحدة فقط قبل البداية</span></div>
                    <h1 className="text-3xl sm:text-4xl font-extrabold mb-4">مرحبًا بك في رحلتك التعليمية{user?.name ? `، ${user.name.split(" ")[0]}` : ""}</h1>
                    <p className="text-[#bcc8cf] text-base leading-relaxed mb-8 max-w-lg">
                      أكمل بعض المعلومات البسيطة حتى نخصّص لك تجربتك داخل المنصة — لن تستغرق أكثر من دقيقة.
                    </p>
                    <button onClick={() => setStep(1)} data-testid="onb-start" className="v2-btn">
                      لنبدأ <ArrowLeft size={18} />
                    </button>
                  </motion.div>
                )}

                {step === 1 && (
                  <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.35 }}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-10 h-10 rounded-2xl bg-[#bb9661]/15 text-[#e3c799] flex items-center justify-center"><Building2 size={20} /></span>
                      <h2 className="text-2xl font-extrabold">اختر إدارتك</h2>
                    </div>
                    <p className="text-[#bcc8cf] text-sm mb-6">اكتب اسم إدارتك وسيتعرّف عليها النظام تلقائيًا حتى لو اختلفت طريقة الكتابة.</p>
                    <DeptPicker value={dept} typed={typedDept} onChange={(d, t) => { setDept(d); setTypedDept(t ?? typedDept); }} testId="onb-dept" />
                    <div className="flex items-center justify-between mt-8">
                      <button onClick={() => setStep(0)} className="v2-btn-ghost v2-btn-sm" data-testid="onb-back-0"><ArrowRight size={15} /> رجوع</button>
                      <button onClick={() => setStep(2)} disabled={!dept} data-testid="onb-next-1"
                        className={`v2-btn ${!dept ? "opacity-40 grayscale cursor-not-allowed" : ""}`}>التالي <ArrowLeft size={17} /></button>
                    </div>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.35 }}>
                    <h2 className="text-2xl font-extrabold mb-2">ما طبيعة مسارك الوظيفي؟</h2>
                    <p className="text-[#bcc8cf] text-sm mb-6">سنجهّز لك الجدارات والمسارات المناسبة لطبيعة عملك.</p>
                    <div className="space-y-3">
                      {TYPES.map((t, i) => {
                        const active = type === t.key;
                        return (
                          <motion.button key={t.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.06 * i }} onClick={() => setType(t.key)} data-testid={`onb-type-${t.key}`}
                            className={`w-full text-start flex items-start gap-4 p-5 rounded-3xl border transition-all ${
                              active ? "border-[#bb9661]/60 bg-[#bb9661]/10 v2-glow-gold" : "border-white/10 bg-white/[0.035] hover:border-white/25"}`}>
                            <span className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${active ? "bg-[#bb9661]/20 text-[#e3c799]" : "bg-white/5 text-[#bcc8cf]"}`}>
                              <t.icon size={24} />
                            </span>
                            <span className="min-w-0">
                              <span className="block font-extrabold text-lg mb-1">{t.label}</span>
                              <span className="block text-sm text-[#bcc8cf] leading-relaxed">{t.desc}</span>
                            </span>
                            {active && <CheckCircle2 className="text-[#91cab8] shrink-0 ms-auto" size={22} />}
                          </motion.button>
                        );
                      })}
                    </div>
                    {type === "manager" && (
                      <p className="text-xs text-[#e3c799] mt-4 v2-inset p-3" data-testid="onb-manager-note">
                        اختيار «مدير إدارة» طلب فقط — تُمنح صلاحيات لوحة الإدارة بعد اعتمادها من إدارة المنصة.
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-8">
                      <button onClick={() => setStep(1)} className="v2-btn-ghost v2-btn-sm" data-testid="onb-back-1"><ArrowRight size={15} /> رجوع</button>
                      <button onClick={submit} disabled={!type || sending} data-testid="onb-submit"
                        className={`v2-btn ${!type || sending ? "opacity-40 grayscale cursor-not-allowed" : ""}`}><Send size={17} /> {sending ? "جارٍ الإرسال..." : "إرسال طلب الانضمام"}</button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
